<script setup lang="ts">
import {computed, onBeforeMount, ref, watch} from "vue";
import {storeToRefs} from "pinia";
import {useLaureateStore} from "@/stores/laureate.ts";
import SeparateFieldsInput from "@/components/fields/SeparateFieldsInput.vue";
import ApiHelper, {API_HELPERS} from "@/helpers/apiHelper.ts";
import axios from "axios";
import AuthService from "@/services/authService.ts";

const autService = new AuthService(),
    laureateStore = useLaureateStore(),
    { laureateInfo, isLoading, errorMessage } = storeToRefs(laureateStore),
    payload = ref<any>({}),
    responseMessage = ref(''),
    sendingForm = ref(false),
    formSent = ref(false),
    errors = ref<any>({}),
    backendErrors = ref<any>({});

const COLUMNS_NUMBER: Record<string, number> = {
    'fullWidth': 1,
    'twoColumns': 2,
    'threeColumns': 3,
};

const prizeFormTemplate = computed(() => (laureateInfo.value as any)?.prize_form?.template ?? []);

const checkPostcode = (event: any) => {
    const code = event.target.value;
    const key = event.keyCode || event.charCode;

    if (event.target.value.length == 2) {
        if( key == 8 || key == 46 ){
        }
        else{
            event.target.value = (code + '-');
        }
    }
    if (event.target.value.indexOf('--') !== -1) {
        event.target.value =(code.replace('--','-'));
    }
}

const checkDate = (event: any) => {
    const splittedDateItems = event.target.value.split('-');
    if (splittedDateItems.length == 3) {
        if (splittedDateItems[0].length > 4) {
            splittedDateItems[0] = splittedDateItems[0].slice(0,4);
            event.target.value = splittedDateItems.join('-');
        }
    }
}

const checkPhone = (fieldName: string, event: any) => {
    const phone = event.target.value;
    const regex = /^(?:(?:(?:(?:\+|00)\d{2})?[ -]?(?:(?:\(0?\d{2}\))|(?:0?\d{2})))?[ -]?(?:\d{3}[- ]?\d{2}[- ]?\d{2}|\d{2}[- ]?\d{2}[- ]?\d{3}|\d{7})|(?:(?:(?:\+|00)\d{2})?[ -]?\d{3}[ -]?\d{3}[ -]?\d{3}))$/;
    if (!regex.test(phone)) {
        errors.value[fieldName] = 'Podaj prawidłowy numer telefonu';
    } else {
        errors.value[fieldName] = '';
    }
}

const checkBankAccount = (obj: any) => {
    const bankAccount = obj.value;
    const regex = /^[0-9]*$/;
    if (!regex.test(bankAccount) || bankAccount.length != 26) {
        errors.value[obj.field_name] = 'Podaj prawidłowy numer konta';
    } else {
        errors.value[obj.field_name] = '';
    }
}

const onInvalidSubmit = () => {
    const firstErrorFieldName = Object.keys(errors.value)[0];
    const element = document.querySelector(`[name="${firstErrorFieldName}"]`);

    if (element) {
        (element as HTMLElement).scrollIntoView({ block: 'center' });
    }
}

const setInvalidField = (field: any, event?: Event) => {
    const target = event?.target as HTMLInputElement | HTMLSelectElement | null;
    const validity = target?.validity;
    let message = 'To pole jest wymagane';

    if (validity && !validity.valueMissing) {
        if (field?.type === 'email' || validity.typeMismatch) {
            message = 'Podaj prawidłowy adres e-mail';
        } else if (field?.type === 'phone') {
            message = 'Podaj prawidłowy numer telefonu';
        } else if (field?.type === 'zip_code' || validity.patternMismatch) {
            message = 'Podaj prawidłowy kod pocztowy';
        } else if (field?.type === 'date') {
            message = 'Podaj prawidłową datę';
        } else if (
            field?.type === 'number' ||
            field?.type === 'range' ||
            validity.rangeOverflow ||
            validity.rangeUnderflow
        ) {
            message = 'Podaj prawidłową wartość';
        }
    }

    errors.value[field.name ?? field] = message;
    onInvalidSubmit();
}

const clearError = (name: string) => {
    if (errors.value[name]) {
        delete errors.value[name];
    }
}

const submitForm = async () => {
    for (let formLayoutIndex in prizeFormTemplate.value) {
        let formLayout = prizeFormTemplate.value[formLayoutIndex];
        for (let fieldIndex in formLayout.data?.fields) {
            let field = formLayout.data?.fields[fieldIndex];
            if (field.is_required && (!payload.value[field.name] || payload.value[field.name] == '')) {
                errors.value[field.name] = 'To pole jest wymagane';
            }
        }
    }

    const hasErrors = Object.values(errors.value).some(x => x !== null && x !== '');

    if (hasErrors) {
        onInvalidSubmit();
        return;
    }

    if (!hasErrors) {
        const data = {
            'name': payload.value['name'],
            'surname': payload.value['surname'],
            'birth_date': payload.value['birth_date'],
            'payload': JSON.stringify(payload.value),
        };

        sendingForm.value = true;
        const token = await autService.getToken();
        await axios.put(ApiHelper.getLaureateSaveUrl(), data, {
            headers: {'X-Form-Token': token}
        })
            .then(response => {
                errors.value = {};
                backendErrors.value = {};
                responseMessage.value = '';
                sendingForm.value = false;

                if (response.status == 200) {
                    formSent.value = true;
                    responseMessage.value = 'Dziękujemy za przesłanie formularza.';
                } else {
                    responseMessage.value = 'Podczas wysyłania formularza wystąpił błąd.';
                }
                window.scrollTo(0,0);
            }).catch(error => {
                if (error.response?.status == 422 && error.response?.data?.errors) {
                    backendErrors.value = error.response.data.errors;
                    errors.value = {}
                } else {
                    backendErrors.value = {}
                    errors.value = {}
                    responseMessage.value = 'Podczas wysyłania formularza wystąpił błąd.';
                }

                sendingForm.value = false;
            });
    }
}

onBeforeMount(async () => {
    await laureateStore.getLaureateInfo();
})

watch(laureateInfo, (newVal) => {
    payload.value['email'] = (newVal as any)?.email;
})
</script>

<template>
    <div class="container mx-auto pb-12 text-white">
        <h1 class="text-[2.5rem] lg:text-6xl text-default-white text-center pt-16 pb-4 lg:pb-8">FORMULARZ<br class="block lg:hidden"/> LAUREATA</h1>
        <div class="w-28 lg:w-52 h-px bg-default-white/70 mx-auto mb-14"></div>
        <div v-if="isLoading" class="text-center min-h-[300px]">
            <div class="text-center m-auto loader"></div>
        </div>
        <template v-else-if="!formSent && Object.keys(laureateInfo).length !== 0 && !isLoading">
            <div v-show="responseMessage" class="message-box text-center text-white text-sm">{{ responseMessage }}</div>
            <form class="pt-4" @submit.prevent="submitForm">
                <div class="flex flex-col items-center gap-8 contact-items pb-12 px-12">
                    <div v-for="formLayout in prizeFormTemplate" class="w-full">
                        <div v-if="formLayout.data?.header" class="text-xl text-default-white py-4 font-light">{{ formLayout.data?.header }}</div>
                        <div class="w-full grid gap-8"
                             :class="{'lg:grid-cols-2': COLUMNS_NUMBER[formLayout.type] === 2, 'lg:grid-cols-3': COLUMNS_NUMBER[formLayout.type] === 3}"
                        >
                            <div v-for="field in formLayout.data?.fields"
                                 class="flex flex-col gap-1"
                            >
                                <template v-if="field.type === 'checkbox'">
                                    <div class="flex gap-2">
                                        <input type="checkbox"
                                               class="appearance-none w-4 h-4 bg-default-white shrink-0"
                                               :class="{'input-error': (errors && errors[field.name]) || (backendErrors && backendErrors[field.name])}"
                                               v-model="payload[field.name]"
                                               :name="field.name"
                                               :required="field.is_required"
                                               @invalid.prevent="setInvalidField(field, $event)"
                                               @change.prevent="clearError(field.name)"
                                        />
                                        <label class="text-sm text-default-white" v-html="field.label"></label>
                                    </div>
                                </template>
                                <template v-else>
                                    <label class="text-sm text-default-white">{{ field.label }}</label>

                                    <input v-if="field.type === 'number' || field.type === 'range'" type="number"
                                           class="block w-full px-1 py-2.5"
                                           :class="{'input-error': (errors && errors[field.name]) || (backendErrors && backendErrors[field.name])}"
                                           v-model="payload[field.name]"
                                           :name="field.name"
                                           :min="field.min ?? undefined"
                                           :max="field.max ?? undefined"
                                           :required="field.is_required"
                                           :placeholder="field.placeholder ?? ''"
                                           @invalid.prevent="setInvalidField(field, $event)"
                                           @change.prevent="clearError(field.name)"
                                    />
                                    <input v-else-if="field.type === 'date'" type="date"
                                           class="block w-full px-1 py-2.5"
                                           :class="{'input-error': (errors && errors[field.name]) || (backendErrors && backendErrors[field.name])}"
                                           v-model="payload[field.name]"
                                           :name="field.name"
                                           :required="field.is_required"
                                           :placeholder="field.placeholder ?? ''"
                                           @keyup.prevent="checkDate"
                                           @invalid.prevent="setInvalidField(field, $event)"
                                           @change.prevent="clearError(field.name)"
                                           min="1000-01-01"
                                           max="9999-12-31"
                                    />
                                    <input v-else-if="field.type === 'email'" type="email"
                                           class="block w-full px-1 py-2.5"
                                           :class="{'input-error': (errors && errors[field.name]) || (backendErrors && backendErrors[field.name])}"
                                           v-model="payload[field.name]"
                                           :name="field.name"
                                           :required="field.is_required"
                                           :placeholder="field.placeholder ?? ''"
                                           :maxlength="field.max_char ?? 255"
                                           @invalid.prevent="setInvalidField(field, $event)"
                                           @change.prevent="clearError(field.name)"
                                    />
                                    <input v-else-if="field.type === 'zip_code'" type="text"
                                           class="postcode block w-full px-1 py-2.5"
                                           :class="{'input-error': (errors && errors[field.name]) || (backendErrors && backendErrors[field.name])}"
                                           v-model="payload[field.name]"
                                           :name="field.name"
                                           :required="field.is_required"
                                           :placeholder="field.placeholder ?? ''"
                                           pattern="[0-9]{2}[\-][0-9]{3}"
                                           @keyup.prevent="checkPostcode"
                                           @invalid.prevent="setInvalidField(field, $event)"
                                           @change.prevent="clearError(field.name)"
                                    />
                                    <input v-else-if="field.type === 'phone'" type="tel"
                                           class="block w-full px-1 py-2.5"
                                           :class="{'input-error': (errors && errors[field.name]) || (backendErrors && backendErrors[field.name])}"
                                           v-model="payload[field.name]"
                                           :name="field.name"
                                           :required="field.is_required"
                                           :placeholder="field.placeholder ?? ''"
                                           @focusout="checkPhone(field.name, $event)"
                                           @invalid.prevent="setInvalidField(field, $event)"
                                           @change.prevent="clearError(field.name)"
                                    />
                                    <div v-else-if="field.type === 'select'"
                                         class="flex flex-row justify-between"
                                    >
                                        <label v-for="option in field.options"
                                               :key="`${field.name}-${option.value}`"
                                               class="flex items-center gap-3"
                                        >
                                            <input type="radio"
                                                   class="laureate-radio-input"
                                                   v-model="payload[field.name]"
                                                   :name="field.name"
                                                   :value="option.value"
                                                   :required="field.is_required"
                                                   @invalid.prevent="setInvalidField(field, $event)"
                                                   @change="clearError(field.name)"
                                            />
                                            <span class="laureate-radio"
                                                  :class="{'input-error': (errors && errors[field.name]) || (backendErrors && backendErrors[field.name])}"
                                                  aria-hidden="true"></span>
                                            <span class="text-default-white">{{ option.name }}</span>
                                        </label>
                                    </div>
                                    <SeparateFieldsInput v-else-if="field.type === 'bank_account'"
                                                         input-mode="numeric"
                                                         :fields="26"
                                                         :field-name="field.name"
                                                         :error="(errors && errors[field.name]) || (backendErrors && backendErrors[field.name])"
                                                         v-model="payload[field.name]"
                                                         :required="field.is_required"
                                                         :special-gaps-after="[2,6,10,14,18,22]"
                                                         @update:validateValue="checkBankAccount"
                                                         @invalid="setInvalidField(field, $event)"
                                    />
                                    <input v-else type="text"
                                           class="block w-full px-1 py-2.5"
                                           :class="{'input-error': (errors && errors[field.name]) || (backendErrors && backendErrors[field.name])}"
                                           v-model="payload[field.name]"
                                           :name="field.name"
                                           :required="field.is_required"
                                           :placeholder="field.placeholder ?? ''"
                                           :maxlength="field.max_char ?? 255"
                                           @invalid.prevent="setInvalidField(field, $event)"
                                           @change.prevent="clearError(field.name)"
                                    />
                                </template>

                                <div v-show="(errors && errors[field.name]) || (backendErrors && backendErrors[field.name])" class="text-default-red error-label">
                                    <template v-if="errors && errors[field.name]">
                                        {{ Array.isArray(errors[field.name]) ? errors[field.name][0] : errors[field.name] }}
                                    </template>
                                    <template v-else>
                                        {{ Array.isArray(backendErrors[field.name]) ? backendErrors[field.name][0] : backendErrors[field.name] }}
                                    </template>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="flex flex-col gap-4 pt-8">
                        <div class="flex gap-2">
                            <input type="checkbox"
                                   id="regulation_1"
                                   name="declaration_of_adulthood"
                                   v-model="payload['declaration_of_adulthood']"
                                   class="w-4 h-4 bg-default-white shrink-0"
                                   :class="{'input-error': (errors && errors['declaration_of_adulthood']) || (backendErrors && backendErrors['declaration_of_adulthood'])}"
                                   required
                                   @invalid.prevent="setInvalidField('declaration_of_adulthood', $event)"
                                   @change.prevent="clearError('declaration_of_adulthood')"
                            />
                            <label for="regulation_1">* Oświadczam, że mam ukończone 18 lat.</label>
                        </div>
                        <div class="flex gap-2">
                            <input type="checkbox"
                                   id="regulation_2"
                                   name="acceptance_of_the_regulations"
                                   v-model="payload['acceptance_of_the_regulations']"
                                   class="appearance-none w-4 h-4 bg-default-white shrink-0"
                                   :class="{'input-error': (errors && errors['acceptance_of_the_regulations']) || (backendErrors && backendErrors['acceptance_of_the_regulations'])}"
                                   required
                                   @invalid.prevent="setInvalidField('acceptance_of_the_regulations', $event)"
                                   @change.prevent="clearError('acceptance_of_the_regulations')"
                            />
                            <label for="regulation_2">* Oświadczam, że <a class="text-default-white" href="/Regulamin.pdf" download>Regulamin</a> Konkursu „CIAO, EMOZIONI 2026” jest mi znany i akceptuję jego treść oraz że spełniam warunki przewidziane w §2 Regulamin Konkursu.</label>
                        </div>
                        <div class="flex gap-2">
                            <input type="checkbox"
                                   id="regulation_4"
                                   name="confirmation_of_data_processing_knowledge"
                                   v-model="payload['confirmation_of_data_processing_knowledge']"
                                   class="appearance-none w-4 h-4 bg-default-white shrink-0"
                                   :class="{'input-error': (errors && errors['confirmation_of_data_processing_knowledge']) || (backendErrors && backendErrors['confirmation_of_data_processing_knowledge'])}"
                                   required
                                   @invalid.prevent="setInvalidField('confirmation_of_data_processing_knowledge', $event)"
                                   @change.prevent="clearError('confirmation_of_data_processing_knowledge')"
                            />
                            <label for="regulation_4">* Oświadczam, iż zostałem poinformowany o&nbsp;zasadach przetwarzania moich danych osobowych, określonych <a class="text-default-white" href="/Regulamin.pdf" download>Regulaminem</a> Konkursu „CIAO, EMOZIONI 2026”.</label>
                        </div>
                        <div class="flex gap-2">
                            <input type="checkbox"
                                   id="regulation_5"
                                   v-model="payload['data_processing_consent']"
                                   class="appearance-none w-4 h-4 bg-default-white shrink-0"
                            />
                            <label for="regulation_5">
                                Wyrażam zgodę na otrzymywanie od Kompanii Piwowarskiej S.A. z siedzibą w Warszawie informacji o produktach, promocjach i wydarzeniach (marketing bezpośredni) za pośrednictwem podanego przeze mnie adresu e-mail. Mam świadomość, że wyrażenie powyższej zgody jest dobrowolne i odbywa się na podstawie art. 398 ustawy Prawo Komunikacji Elektronicznej. Zgoda może zostać wycofana w dowolnym momencie, co nie wpływa na zgodność z prawem komunikacji zrealizowanej przed ich wycofaniem.
                            </label>
                        </div>
                        <div class="regulations-items pt-6 text-white">* Oświadczenie jest obowiązkowe</div>
                        <div v-show="(errors
                            && (errors.declaration_of_adulthood || errors.acceptance_of_the_regulations || errors.confirmation_of_data_processing_knowledge))
                            || (backendErrors
                            && (backendErrors.declaration_of_adulthood || backendErrors.acceptance_of_the_regulations || backendErrors.confirmation_of_data_processing_knowledge))"
                             class="text-default-red error-label text-left">
                            Zaznacz wymagane zgody.
                        </div>
                    </div>
                    <div class="text-xs lg:text-sm text-left pt-10 max-w-[1100px] mx-auto font-light">
                        Informacja o przetwarzaniu danych: Administratorem Twoich danych osobowych jest Kompania Piwowarska S.A. z siedzibą w Warszawie. Twoje dane przetwarzamy w celu przygotowania, wysyłki oraz analizy skuteczności komunikacji marketingowej na podstawie naszego prawnie uzasadnionego interesu (art. 6 ust. 1 lit. f RODO) w postaci promocji naszych marek i utrzymywania kontaktu z osobami zainteresowanymi naszą działalnością. Przysługuje Ci prawo do wglądu w dane, ich poprawiania, żądania usunięcia oraz wniesienia sprzeciwu. Szczegóły znajdziesz w Polityce Prywatności KP.
                    </div>
                </div>
                <button class="block w-[80%] lg:w-[40%]" :disabled="sendingForm">
                    <span v-if="!sendingForm">WYŚLIJ</span>
                    <span v-else class="animate__animated animate__fadeIn">WYSYŁANIE...</span>
                </button>
            </form>
        </template>
        <template v-else-if="!formSent && Object.keys(laureateInfo).length === 0 && !isLoading">
            <div class="text-center text-xl px-4">{{errorMessage}}</div>
        </template>
        <template v-else-if="formSent">
            <div class="text-center text-xl px-4">{{ responseMessage }}</div>
        </template>
    </div>
</template>

<style scoped>
input {
    background: var(--color-default-white);
    font-size: 1rem;
    color: var(--color-primary);
}

input::placeholder, label, input, .regulations-items, .message-box, .error-label {
    font-weight: 300;
}

input::placeholder {
    color: var(--color-secondary);
}

select {
    background: var(--color-default-white);
    width: 100%;
    font-size: 1rem;
    color: var(--color-primary);
}

.error-label {
    font-size: 0.8rem;
    text-align: right;
}

button, a[role="button"] {
    font-weight: 300;
    padding: 1rem 2rem;
    margin: auto;
    font-size: 2rem;
}

.laureate-radio-input {
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;
}

.laureate-radio {
    display: inline-block;
    width: 2rem;
    height: 2rem;
    flex-shrink: 0;
    background: var(--color-default-white);
    border: 2px solid transparent;
    cursor: pointer;
    position: relative;
}

.laureate-radio::before {
    content: "";
    position: absolute;
    inset: 0.4375rem;
    background: var(--color-default-red);
    transform: scale(0);
    transition: 120ms transform ease-in-out;
}

.laureate-radio-input:checked + .laureate-radio::before {
    transform: scale(1);
}

.laureate-radio-input:focus-visible + .laureate-radio {
    outline: 2px solid var(--color-default-red);
    outline-offset: 2px;
}
</style>
